import * as React from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button.jsx'

/**
 * Copy to clipboard, with a confirmation state.
 *
 * ── WHY THE CONFIRMATION IS NOT DECORATION ──────────────────────────────────
 * This is the most-pressed control in the onboarding funnel, and a copy that
 * gives no feedback is indistinguishable from one that failed. A contractor who
 * is not sure it worked presses it again, then pastes twice, then doubts the
 * whole screen. The confirmation is what makes the funnel feel reliable.
 *
 * `navigator.clipboard` needs a secure context — it is unavailable on plain
 * HTTP beyond localhost, which is exactly where a self-hosted or
 * badly-proxied deployment ends up. The fallback keeps the funnel working
 * there rather than silently doing nothing, and a hard failure surfaces a
 * select-and-copy prompt instead of pretending.
 */
export function CopyButton ({ value, label = 'Copy', copiedLabel = 'Copied', ...props }) {
  const [state, setState] = React.useState('idle')
  const timer = React.useRef(null)

  React.useEffect(() => () => clearTimeout(timer.current), [])

  async function copy () {
    const ok = await writeClipboard(value)
    setState(ok ? 'copied' : 'failed')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setState('idle'), 2000)
  }

  return (
    <Button
      type="button"
      variant={state === 'copied' ? 'secondary' : 'primary'}
      onClick={copy}
      // Announced politely so a screen-reader user gets the same confirmation
      // a sighted user gets from the tick.
      aria-live="polite"
      data-copied={state === 'copied' || undefined}
      {...props}
    >
      {state === 'copied' ? <Check /> : <Copy />}
      {state === 'copied' ? copiedLabel : state === 'failed' ? 'Press Ctrl+C' : label}
    </Button>
  )
}

async function writeClipboard (text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through — a permissions refusal is not a reason to give up.
  }

  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}
